import { type RemoteMission } from "@rescuetablet/missions-client";
import { useMissions } from "@rescuetablet/missions-client/react";
import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { notEmpty } from "../utils";

export type ActiveMission = {
  missionId: string;
  sectionId?: string;
  pinnedSectionId?: string;
  pinnedResourceId?: string;
};

type ActiveMissionStore = {
  active?: ActiveMission;
  setActiveMission: (
    updater: (current?: ActiveMission) => ActiveMission | undefined,
  ) => void;
};

const useActiveMissionStore = create<ActiveMissionStore>((set) => ({
  setActiveMission: (updater) =>
    set(({ active }) => ({ active: updater(active) })),
}));

export function useActiveMission(): ActiveMission | undefined {
  return useActiveMissionStore((store) => store.active);
}

export function useSetActiveMission(): (
  updater: (current?: ActiveMission) => ActiveMission | undefined,
) => void {
  return useActiveMissionStore((store) => store.setActiveMission);
}

export function useCurrentMission(): RemoteMission | undefined {
  const missions = useMissions();
  const active = useActiveMission();
  const mission = active ? missions[active.missionId] : undefined;
  return mission?.state === "ready" ? mission.mission : undefined;
}

export function useLoadedMissions(): ReadonlyArray<RemoteMission> {
  const missions = useMissions();

  return useMemo(
    () =>
      Object.values(missions)
        .map((m) => (m.state === "ready" ? m.mission : undefined))
        .filter(notEmpty),
    [missions],
  );
}

export function useTrackActiveMission(missionId: string) {
  const setActiveMission = useSetActiveMission();

  useEffect(() => {
    setActiveMission((active) =>
      active?.missionId === missionId ? active : { missionId },
    );
  }, [missionId, setActiveMission]);
}
